"use client";

import { ChevronDown, Copy } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DATA_TABLE_ROW_TOOL_OPTIONS,
  getDataTableAllowedTools,
  resolveDataTableAccessMode,
} from "./constants";

interface DataTableAvailableOptionsProps {
  accessMode: DataTableAccessMode | null | undefined;
}

export function DataTableAvailableOptions({
  accessMode,
}: DataTableAvailableOptionsProps) {
  const resolvedMode = resolveDataTableAccessMode(accessMode);
  const tools = getDataTableAllowedTools(resolvedMode);

  if (!tools.length) {
    return null;
  }

  const toolMetaByValue = new Map(
    DATA_TABLE_ROW_TOOL_OPTIONS.map((option) => [option.value, option])
  );

  return (
    <Collapsible className="w-full">
      <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md bg-muted/50 p-3 text-sm font-medium transition-colors hover:bg-muted/70">
        <span>Available actions</span>
        <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="max-h-72 overflow-y-auto">
          <div className="grid grid-cols-1 gap-2 pt-3 md:grid-cols-2">
            {tools.map((tool) => {
              const meta = toolMetaByValue.get(tool);
              return (
                <div
                  key={tool}
                  className="flex items-start gap-2 rounded-md bg-muted/50 p-2"
                >
                  <div className="mt-1 min-w-[16px]">
                    <div className="h-2 w-2 rounded-full bg-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {meta?.label ?? tool}
                    </p>
                    {meta?.description ? (
                      <p className="text-xs text-muted-foreground">
                        {meta.description}
                      </p>
                    ) : null}
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="mt-1 h-6 w-6 rounded-full border-muted p-0 transition hover:bg-muted"
                        onClick={async () => {
                          await navigator.clipboard.writeText(tool);
                          toast.success("Tool slug copied to clipboard");
                        }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="top"
                      align="end"
                      className="text-[10px]"
                    >
                      {tool}
                    </TooltipContent>
                  </Tooltip>
                </div>
              );
            })}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
